import React, { useState } from "react";
import { ArrowUpRight, Calculator, Copy, FileText, Gavel, Reply, SmilePlus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/common";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DocIcon } from "@/components/canvas/LosBits";
import { AuthImage } from "@/components/meldinger/bits";
import { clock, dayLabel, fmtSize, groupMessages } from "@/lib/meldinger";

export const REACTIONS = ["👍", "✅", "👀", "❤️", "🙏"];

/** Text with @-mentions set in navy – no chips below the message. */
function Rich({ text, mentions = [] }) {
  if (!text) return null;
  const names = mentions.map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length) return <>{text}</>;
  const re = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text))) { if (m.index > last) parts.push(text.slice(last, m.index)); parts.push(<span key={m.index} className="rounded-[4px] bg-[hsl(var(--navy)/0.08)] px-1 font-medium text-[hsl(var(--navy))]">@{m[1]}</span>); last = m.index + m[0].length; }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/** A shared work object – the actual post / calculation / document, not a pasted copy. */
export function ShareChip({ s, onOpenItem, onOpenKalkyle, onOpenDoc, testId }) {
  const kalkyle = s.kind === "kalkyle";
  const open = () => (s.kind === "doc" ? onOpenDoc?.(s.id) : kalkyle ? onOpenKalkyle?.(s.ref || s.id) : onOpenItem?.(s.ref || s.id));
  return (
    <button type="button" onClick={open} className="group flex w-full max-w-[420px] items-center gap-3 rounded-[12px] border border-black/[0.08] bg-white px-3 py-2.5 text-left shadow-[0_1px_2px_hsl(var(--shadow-color)/0.04)] transition-colors hover:border-black/[0.16]" data-testid={testId} data-kind={s.kind}>
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px]", kalkyle ? "bg-[hsl(var(--navy)/0.07)] text-[hsl(var(--navy))]" : "bg-[hsl(210_20%_96%)] text-muted-foreground")}>{s.kind === "doc" ? <FileText className="h-4 w-4" /> : kalkyle ? <Calculator className="h-4 w-4" /> : <span className="font-mono text-[11px] font-semibold">{s.ref}</span>}</span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[13px] font-medium text-foreground">{kalkyle ? `Kalkyle · ${s.ref}` : s.kind === "item" && s.ref ? `${s.ref} · ${s.title}` : s.title}</span>
        <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{s.summary || (kalkyle ? s.title : s.kind === "doc" ? "Dokument på saken" : "Arbeidspost")}</span>
      </span>
      <span className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-medium text-[hsl(var(--navy))] opacity-70 group-hover:opacity-100">{s.kind === "doc" ? "Åpne" : kalkyle ? "Åpne i kalkyle" : "Åpne post"} <ArrowUpRight className="h-3.5 w-3.5" /></span>
    </button>
  );
}

function Attachments({ list, onZoom }) {
  const images = list.filter((a) => a.kind === "image");
  const files = list.filter((a) => a.kind !== "image");
  return (
    <div className="space-y-2">
      {images.length ? (
        <div className={cn("grid gap-1.5", images.length === 1 ? "grid-cols-1" : "grid-cols-2")} data-testid="message-images">
          {images.map((a) => <AuthImage key={a.id} url={a.url} alt={a.name} onClick={() => onZoom(a)} className={cn("w-full bg-black/[0.03]", images.length === 1 ? "max-h-[360px] max-w-[440px]" : "aspect-[4/3] max-w-[220px]")} testId={`message-image-${a.id}`} />)}
        </div>
      ) : null}
      {files.map((a) => <FileCard key={a.id} a={a} />)}
    </div>
  );
}

function FileCard({ a }) {
  const open = async () => {
    try {
      const { api } = await import("@/lib/api");
      const r = await api.get(a.url, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Kunne ikke åpne filen"); }
  };
  return (
    <button type="button" onClick={open} className="group flex w-full max-w-[380px] items-center gap-3 rounded-[12px] border border-black/[0.08] bg-white px-3 py-2.5 text-left transition-colors hover:border-black/[0.16]" data-testid={`message-file-${a.id}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[hsl(210_20%_96%)] text-muted-foreground"><DocIcon kind={(a.name || "").split(".").pop()} className="h-4 w-4 text-current" /></span>
      <span className="min-w-0 flex-1 leading-tight"><span className="block truncate text-[13px] font-medium">{a.name}</span><span className="mt-0.5 block text-[12px] text-muted-foreground">{[fmtSize(a.size), (a.name || "").split(".").pop()?.toUpperCase()].filter(Boolean).join(" · ")}</span></span>
    </button>
  );
}

function Reactions({ m, onReact, canWrite }) {
  if (!m.reactions?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`message-reactions-${m.id}`}>
      {m.reactions.map((r) => <button key={r.emoji} type="button" disabled={!canWrite} onClick={() => onReact(m.id, r.emoji)} title={r.names.join(", ")} className={cn("inline-flex h-6 items-center gap-1 rounded-full border px-1.5 text-[12px] transition-colors", r.mine ? "border-[hsl(var(--navy)/0.35)] bg-[hsl(var(--navy)/0.06)]" : "border-black/[0.08] bg-white hover:border-black/[0.18]")} data-testid={`message-reaction-${m.id}-${r.emoji}`} aria-pressed={r.mine}>{r.emoji}<span className="font-mono text-[10.5px] tabular-nums text-foreground/70">{r.count}</span></button>)}
    </div>
  );
}

function Actions({ m, onReply, onReact, canWrite, mine }) {
  const [open, setOpen] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(m.content || ""); toast.success("Tekst kopiert"); } catch { toast("Kunne ikke kopiere"); } };
  if (!canWrite) return null;
  return (
    <div className={cn("absolute -top-3 z-10 flex items-center gap-px rounded-full border border-black/[0.08] bg-white p-0.5 shadow-[0_2px_8px_hsl(var(--shadow-color)/0.12)] transition-opacity", open ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100", mine ? "left-2" : "right-2")} data-testid={`message-actions-${m.id}`}>
      <button type="button" onClick={() => onReply(m)} className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-foreground/70 hover:bg-black/[0.05] hover:text-foreground" data-testid={`message-reply-${m.id}`}><Reply className="h-3.5 w-3.5" /> Svar</button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><button type="button" className="flex h-7 w-7 items-center justify-center rounded-full text-foreground/70 hover:bg-black/[0.05] hover:text-foreground" aria-label="Reager" data-testid={`message-react-${m.id}`}><SmilePlus className="h-3.5 w-3.5" /></button></PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} className="flex w-auto gap-0.5 rounded-full border-black/[0.08] p-1 shadow-float" aria-describedby={undefined}>
          {REACTIONS.map((e) => <button key={e} type="button" onClick={() => { onReact(m.id, e); setOpen(false); }} className="flex h-8 w-8 items-center justify-center rounded-full text-[16px] transition-transform hover:scale-110 hover:bg-black/[0.05]" data-testid={`message-react-${m.id}-${e}`}>{e}</button>)}
        </PopoverContent>
      </Popover>
      <button type="button" onClick={copy} className="flex h-7 w-7 items-center justify-center rounded-full text-foreground/70 hover:bg-black/[0.05] hover:text-foreground" aria-label="Kopier tekst" data-testid={`message-copy-${m.id}`}><Copy className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Bubble({ m, mine, canWrite, onReply, onReact, onZoom, openers }) {
  const decision = m.kind === "beslutning";
  const hasBody = Boolean(m.content);
  return (
    <div className={cn("group relative flex", mine ? "justify-end" : "justify-start")} data-testid={`thread-message-${m.id}`} data-kind={m.kind} data-mine={mine ? "1" : "0"}>
      <div className={cn("relative max-w-[min(100%,680px)] space-y-1.5", mine ? "items-end" : "items-start")}>
        <Actions m={m} onReply={onReply} onReact={onReact} canWrite={canWrite} mine={mine} />
        {m.reply_to ? (
          <div className={cn("flex items-start gap-2 border-l-2 border-black/[0.15] pl-2.5 text-[12.5px] text-muted-foreground", mine && "ml-auto")} data-testid={`message-quote-${m.id}`}>
            <span className="min-w-0"><span className="font-medium text-foreground/70">Svar til {m.reply_to.author?.name?.split(" ")[0] || "melding"}</span><span className="block truncate italic">«{m.reply_to.preview}»</span></span>
          </div>
        ) : null}
        {hasBody ? (
          <div className={cn("whitespace-pre-wrap text-[14px] leading-[1.6] [overflow-wrap:anywhere]", decision ? "rounded-[14px] bg-[hsl(var(--navy)/0.05)] px-3.5 py-2.5 ring-1 ring-[hsl(var(--navy)/0.18)]" : mine ? "rounded-[18px] rounded-br-[6px] bg-[hsl(var(--navy)/0.07)] px-3.5 py-2 text-foreground" : "text-foreground")} data-testid={`message-body-${m.id}`}>
            {decision ? <span className="mb-1 flex items-center gap-1 text-[11.5px] font-medium text-[hsl(var(--navy))]"><Gavel className="h-3 w-3" /> Avklart</span> : null}
            <Rich text={m.content} mentions={m.mentions} />
          </div>
        ) : null}
        {m.attachments?.length ? <Attachments list={m.attachments} onZoom={onZoom} /> : null}
        {m.shares?.length ? <div className="space-y-1.5">{m.shares.map((s, i) => <ShareChip key={`${s.kind}-${s.id}-${i}`} s={s} {...openers} testId={`message-share-${m.id}-${i}`} />)}</div> : null}
        <Reactions m={m} onReact={onReact} canWrite={canWrite} />
      </div>
    </div>
  );
}

/** Messages the calm way: day markers, author once per run, Egil to the right in a faint bubble, others left with almost no frame. */
export function MessageStream({ messages, me, canWrite, onReply, onReact, onZoom, openers }) {
  const rows = groupMessages(messages);
  return (
    <ol className="mx-auto w-full max-w-[760px] space-y-5" data-testid="thread-message-stream">
      {rows.map((r) => {
        if (r.type === "day") return <li key={r.key} className="flex items-center gap-3 py-1 text-[11.5px] font-medium text-muted-foreground/70" data-testid="thread-day"><span className="h-px flex-1 bg-black/[0.06]" />{dayLabel(r.at)}<span className="h-px flex-1 bg-black/[0.06]" /></li>;
        const mine = r.author?.id === me;
        const a = r.author || {};
        return (
          <li key={r.key} className={cn("flex gap-3", mine ? "flex-row-reverse" : "flex-row")} data-testid={`thread-group-${r.key}`}>
            {!mine ? <UserAvatar name={a.name} initials={a.initials} color={a.color} src={a.avatar_url} size="md" className="mt-0.5 h-8 w-8 shrink-0 text-[11px]" /> : null}
            <div className={cn("min-w-0 flex-1 space-y-1", mine && "flex flex-col items-end")}>
              <div className={cn("flex items-baseline gap-2 text-[12px] text-muted-foreground", mine && "flex-row-reverse")}>
                <span className="font-semibold text-foreground">{mine ? "Du" : a.name || "Ukjent"}</span>
                <time dateTime={r.at}>{clock(r.at)}</time>
              </div>
              <div className={cn("w-full space-y-1.5", mine && "flex flex-col items-end")}>
                {r.items.map((m) => <Bubble key={m.id} m={m} mine={mine} canWrite={canWrite} onReply={onReply} onReact={onReact} onZoom={onZoom} openers={openers} />)}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
